import { useCallback, useEffect, useMemo, useState } from 'react';
import Divider from '../common/Divider';
import Modal, { ModalHeader, ModalProps, ModalTitle } from './Modal';

import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch } from '@/state/hooks';
import {
    dismissBankId,
    dismissBankRequestId,
    dismissWalletId,
    dismissWalletRequestId
} from '@/state/reducers/portalPlatformSlice';
import { FlattenPortalRequest } from '@/types/sso';
import useAbortController from '@/utils/hooks/useAbortController';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import { useSso } from '@/utils/providers/SSOProvider';
import axios from 'axios';
import { Button } from '../common/Button';
import ErrorMessage from '../inputs/ErrorMessage';
import BankDetails from '../modules/Portal/Request/BankDetails';
import WalletDetails from '../modules/Portal/Request/WalletDetails';

interface DeactivateBankWalletModalProps extends ModalProps {
    selectedBankAccount?: FlattenPortalRequest;
    selectedWallet?: FlattenPortalRequest;
    mode: 'bank' | 'wallet';
    onRefresh: () => void;
}

const DeactivateBankWalletModal = (props: DeactivateBankWalletModalProps) => {
    const { opened, handlers, mode, selectedBankAccount, selectedWallet, onRefresh } = props;

    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const { jwt, foundEntity } = useSso();
    const { on401Error } = use401Error();

    const [error, setError] = useState({ message: '' });
    const [isLoading, setIsLoading] = useState(false);

    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const shouldDismissBank = useMemo(() => {
        return selectedBankAccount
            ? ['REJECTED', 'CANCELED'].includes(selectedBankAccount.requestStatus) || !selectedBankAccount.active
            : false;
    }, [selectedBankAccount]);

    const shouldDismissWallet = useMemo(() => {
        return selectedWallet
            ? ['REJECTED', 'CANCELED'].includes(selectedWallet.requestStatus) || !selectedWallet.active
            : false;
    }, [selectedWallet]);

    const onSubmit = useCallback(async () => {
        setIsLoading(true);
        try {
            if (portalURL) {
                if (mode === 'bank' && selectedBankAccount) {
                    if (selectedBankAccount.requestStatus === 'INITIATED') {
                        const payload = {
                            requestId: selectedBankAccount?.id,
                            decision: 'CANCEL'
                        };
                        await axios.post(portalAPI('/api/requests/status/update'), payload, {
                            headers: { Authorization: `Bearer ${jwt}` },
                            signal: abortController.signal
                        });
                    } else if (shouldDismissBank) {
                        const requestId = selectedBankAccount?.id;
                        const bankId = selectedBankAccount?.bankInfoId;

                        if (requestId) dispatch(dismissBankRequestId(requestId));
                        else if (bankId) dispatch(dismissBankId(bankId));

                        setIsLoading(false);
                        handlers.close();
                        return;
                    } else {
                        await axios.post(
                            portalAPI('/api/whitelist/bank-info/remove'),
                            {
                                entityId: foundEntity?.entityId,
                                bankInfoId: selectedBankAccount?.bankInfoId,
                                bankName: selectedBankAccount?.bankName,
                                ibanCode: selectedBankAccount?.ibanCode,
                                accountNumber: selectedBankAccount?.accountNumber,
                                swiftCode: selectedBankAccount?.swiftCode,
                                baseCurrencyId: selectedBankAccount?.baseCurrencyId,
                                friendlyName: selectedBankAccount?.friendlyName,
                                bankAddress: selectedBankAccount?.bankAddress,
                                // baseCurrencyName: selectedBankAccount?.baseCurrencyName,
                                correspondentBankName: selectedBankAccount?.correspondentBankName,
                                correspondentBankSwiftCode: selectedBankAccount?.correspondentBankSwiftCode,
                                intermediaryBankName: selectedBankAccount?.intermediaryBankName,
                                intermediaryBankSwiftCode: selectedBankAccount?.intermediaryBankSwiftCode
                            },
                            { headers: { Authorization: `Bearer ${jwt}` }, signal: abortController.signal }
                        );
                    }
                } else if (mode === 'wallet' && selectedWallet) {
                    if (selectedWallet.requestStatus === 'INITIATED') {
                        const payload = {
                            requestId: selectedWallet?.id,
                            decision: 'CANCEL'
                        };
                        await axios.post(portalAPI('/api/requests/status/update'), payload, {
                            headers: { Authorization: `Bearer ${jwt}` },
                            signal: abortController.signal
                        });
                    } else if (shouldDismissWallet) {
                        const requestId = selectedWallet?.id;
                        const walletId = selectedWallet?.walletId;

                        if (requestId) dispatch(dismissWalletRequestId(requestId));
                        else if (walletId) dispatch(dismissWalletId(walletId));
                        setIsLoading(false);
                        handlers.close();
                        return;
                    } else {
                        await axios.post(
                            portalAPI('/api/whitelist/wallet/remove'),
                            {
                                entityId: foundEntity?.entityId,
                                friendlyName: selectedWallet?.friendlyName,
                                networkId: selectedWallet?.networkId,
                                walletId: selectedWallet?.walletId,
                                walletAddress: selectedWallet?.walletAddress
                            },
                            { headers: { Authorization: `Bearer ${jwt}` }, signal: abortController.signal }
                        );
                    }
                }

                onRefresh();
                setIsLoading(false);
                handlers.close();
            }
        } catch (error: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on deactivate bank wallet modal. JWT may be invalid or expired.`
                );
            });
            setError(error?.response?.data || error);
            setIsLoading(false);
        }
    }, [mode, selectedBankAccount, selectedWallet, jwt, foundEntity, onRefresh, onClose]);

    const friendlyName = useMemo(() => {
        return selectedBankAccount?.friendlyName ?? selectedWallet?.friendlyName;
    }, [selectedBankAccount, selectedWallet]);

    const modalTitle = useMemo(() => {
        if (mode === 'bank' && selectedBankAccount) {
            if (selectedBankAccount.requestStatus === 'INITIATED') return lang.portal.cancelBankAccountRequest;
            else if (shouldDismissBank) return lang.portal.dismissBankAccountRequest;
            else return lang.portal.deactivateBankAccount;
        } else if (mode === 'wallet' && selectedWallet) {
            if (selectedWallet.requestStatus === 'INITIATED') return lang.portal.cancelWalletAddressRequest;
            else if (shouldDismissWallet) return lang.portal.dismissWalletAddressRequest;
            else return lang.portal.deactivateWalletAddress;
        }
        return mode === 'bank' ? lang.portal.deactivateBankAccount : lang.portal.deactivateWalletAddress;
    }, [mode, lang, selectedBankAccount, selectedWallet]);

    const description = useMemo(() => {
        if (mode === 'bank' && selectedBankAccount) {
            if (selectedBankAccount.requestStatus === 'INITIATED') return lang.portal.cancelTheRequestFor;
            else if (shouldDismissBank) return lang.notifications.dismiss;
            else return lang.portal.deactivate;
        } else if (mode === 'wallet' && selectedWallet) {
            if (selectedWallet.requestStatus === 'INITIATED') return lang.portal.cancelTheRequestFor;
            else if (shouldDismissWallet) return lang.notifications.dismiss;
            else return lang.portal.deactivate;
        }
        return '';
    }, [mode, lang, selectedBankAccount, selectedWallet, shouldDismissBank, shouldDismissWallet]);

    useEffect(() => {
        if (opened) {
            setError({ message: '' });
            setAbortController(new AbortController());
        } else abortController.abort();

        return () => abortController.abort();
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>{modalTitle}</ModalTitle>
                {/* <ModalClose handlers={handlers} /> */}
            </ModalHeader>
            <Divider neutral />
            <div className="flex flex-col flex-1 basis-0">
                <div className="p-2 sm:p-4 flex flex-col gap-2 text-brand-text w-full h-full overflow-auto min-h-32">
                    <div className="text-xs sm:text-sm">
                        {lang.portal.areYouSureYouWantTo} {description}
                        <b> {friendlyName}</b>?
                    </div>
                    {mode === 'bank' && selectedBankAccount && (
                        <BankDetails.Summary selectedBankAccount={selectedBankAccount} />
                    )}
                    {mode === 'wallet' && selectedWallet && <WalletDetails.Summary selectedWallet={selectedWallet} />}
                    <ErrorMessage className="!p-0" error={error} />
                </div>
                <Divider neutral />
                <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                    <button
                        type="button"
                        className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                        onClick={onClose}>
                        {lang.common.close}
                    </button>
                    <Button
                        type="button"
                        onClick={() => {
                            if (!isLoading) onSubmit();
                        }}
                        isLoading={isLoading}
                        className="w-auto rounded-md p-2 px-4 bg-brand-secondary hover:bg-brand-secondary-light focus:ring-0">
                        {modalTitle}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DeactivateBankWalletModal;
